import React, { useState } from 'react';
import { useTeamPlayers } from '@/hooks/useTeamPlayers';
import { usePlayers } from '@/hooks/usePlayers';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Users, ExternalLink } from 'lucide-react';
import { VerifiedAvatar } from '@/components/ui/verified-avatar';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface TeamRosterManagementProps {
  teamId: string;
  clubId: string;
}

interface ClubMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export const TeamRosterManagement = ({ teamId, clubId }: TeamRosterManagementProps) => {
  const queryClient = useQueryClient();
  const { players: teamPlayers, loading, refetch } = useTeamPlayers(teamId);
  const { players: allPlayers, fetchPlayers } = usePlayers();
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [clubMembers, setClubMembers] = useState<ClubMember[]>([]);
  const [loadingClubMembers, setLoadingClubMembers] = useState(true);


  // Fetch club members with 'player' role when component mounts or clubId changes
  React.useEffect(() => {
    const fetchClubMembers = async () => {
      if (!clubId) return;
      
      try {
        setLoadingClubMembers(true);
        
        // Get all club members with player role
        const { data: clubMemberData, error: memberError } = await supabase
          .from('user_club_roles')
          .select(`
            user_id,
            profiles!inner(
              id,
              first_name,
              last_name,
              email
            )
          `)
          .eq('club_id', clubId)
          .eq('role', 'player')
          .eq('is_active', true);

        if (memberError) {
          console.error('Error fetching club members:', memberError);
          return;
        }

        if (!clubMemberData || clubMemberData.length === 0) {
          setClubMembers([]);
          return;
        }

        // Transform the data to ClubMember format
        const members = clubMemberData.map(member => ({
          id: member.profiles.id,
          first_name: member.profiles.first_name,
          last_name: member.profiles.last_name,
          email: member.profiles.email
        }));

        setClubMembers(members);
        
      } catch (error) {
        console.error('Error in fetchClubMembers:', error);
      } finally {
        setLoadingClubMembers(false);
      }
    };

    fetchClubMembers();
  }, [clubId]);

  // Filter available club members (those not already on this team's roster)
  const availableClubMembers = clubMembers.filter(member => {
    // Check if this club member already has a player profile on this team
    return !teamPlayers.some(player => player.profile_id === member.id);
  });


  const handleAddPlayer = async () => {
    if (!selectedPlayerId) {
      toast.error('Please select a club member');
      return;
    }

    if (!teamId) {
      toast.error('Team ID is missing');
      return;
    }
    
    // Check if this club member is already on this team roster
    const isAlreadyOnTeam = teamPlayers.some(player => player.profile_id === selectedPlayerId);
    if (isAlreadyOnTeam) {
      toast.error('This member is already on the team roster');
      return;
    }
    
    try {
      setIsAdding(true);
      
      // Check if this club member already has a player profile
      const { data: existingPlayer, error: checkError } = await supabase
        .from('players')
        .select('id, profile_id, team_id')
        .eq('profile_id', selectedPlayerId)
        .maybeSingle();
      
      
      
      if (checkError) {
        toast.error('Failed to verify player status');
        return;
      }
      
      let playerId: string;
      
      if (existingPlayer) {
        // Player profile exists, update team_id
        const { data: updateData, error: updateError } = await supabase
          .from('players')
          .update({ team_id: teamId })
          .eq('id', existingPlayer.id)
          .select()
          .single();

        if (updateError) {
          throw updateError;
        }
        
        playerId = existingPlayer.id;
      } else {
        // No player profile exists, create one
        
        const { data: newPlayer, error: createError } = await supabase
          .from('players')
          .insert([{
            profile_id: selectedPlayerId,
            team_id: teamId,
            stats: {
              serves: 0,
              spikes: 0,
              blocks: 0,
              digs: 0,
              assists: 0
            },
            skill_levels: {
              serving: 50,
              spiking: 50,
              blocking: 50,
              passing: 50,
              setting: 50
            },
            games_played: 0
          }])
          .select()
          .single();

        if (createError) {
          throw createError;
        }
        
        playerId = newPlayer.id;
      }
      
      toast.success('Player added to roster successfully');
      setSelectedPlayerId('');
      
      // Refresh all data
      await Promise.all([
        refetch(),
        fetchPlayers(),
        queryClient.invalidateQueries({ queryKey: ['players'] }),
        queryClient.invalidateQueries({ queryKey: ['team-players', teamId] }),
        queryClient.invalidateQueries({ queryKey: ['team-player-count', teamId] })
      ]);
    } catch (error) {
      if (error instanceof Error && error.message) {
        toast.error(`Failed to add player: ${error.message}`);
      } else {
        toast.error('Failed to add player to roster');
      }
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemovePlayer = async (playerId: string) => {
    try {
      const { error } = await supabase
        .from('players')
        .update({ team_id: null })
        .eq('id', playerId);

      if (error) {
        throw error;
      }
      
      toast.success('Player removed from roster successfully');
      
      // Refresh all data
      await Promise.all([
        refetch(),
        fetchPlayers(),
        queryClient.invalidateQueries({ queryKey: ['players'] }),
        queryClient.invalidateQueries({ queryKey: ['team-players', teamId] }),
        queryClient.invalidateQueries({ queryKey: ['team-player-count', teamId] })
      ]);
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player from roster');
    }
  };


  if (loading || loadingClubMembers) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading roster...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add Player Section - Only show if there are available players */}
      {availableClubMembers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Player to Roster
            </CardTitle>
            <CardDescription>
              Assign club members with player role to this team's roster
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-sm font-medium">Select Club Player</label>
                <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a club player..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableClubMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleAddPlayer} 
                disabled={!selectedPlayerId || isAdding}
              >
                {isAdding ? 'Adding...' : 'Add Player'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Roster */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Roster ({teamPlayers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {teamPlayers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Players on Roster</h3>
              <p className="text-muted-foreground">
                Add club players to this team's roster using the form above.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {teamPlayers.map((player) => (
                <div key={player.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <VerifiedAvatar
                      src={player.profile_picture_url || ""}
                      alt={`${player.profile?.first_name || 'Unknown'} ${player.profile?.last_name || 'Player'}`}
                      fallback={`${player.profile?.first_name?.[0] || '?'}${player.profile?.last_name?.[0] || '?'}`}
                      isVerified={player.profile?.is_verified || false}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Link 
                          to={`/superadmin/player/${player.id}`}
                          className="font-medium hover:text-blue-600 hover:underline flex items-center gap-1"
                        >
                          {player.profile?.first_name || 'Unknown'} {player.profile?.last_name || 'Player'}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {player.jersey_number && (
                          <Badge variant="outline">#{player.jersey_number}</Badge>
                        )}
                         <div className="flex items-center gap-1">
                           <span>{player.position || 'No position'}</span>
                         </div>
                        {player.grad_year && (
                          <span>Class of {player.grad_year}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemovePlayer(player.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
